// Mounted once in App.tsx: gives every <SqlBuilderButton> (designerKit) the
// platform's query builder. A host without this provider (the downloaded /
// J2EE runtimes) shows no button at all.
//
// The project is the request's own when the field knows it; otherwise the
// project open in the Workbench, otherwise the one named in the URL
// (/designers/<kind>/<project>/…, /workbench/<project>/…).
import { lazy, Suspense, useCallback, useMemo, useState, type ReactNode } from 'react'
import toast from 'react-hot-toast'
import {
  SqlBuilderLauncherContext, type SqlBuilderLauncher, type SqlBuilderRequest,
} from '@twasta/page-runtime/designerKit'
import { useProjectStore } from '@/store/projectStore'

const QueryBuilderModal = lazy(() => import('./QueryBuilderModal'))

function projectFromUrl(): string {
  const parts = window.location.pathname.split('/').filter(Boolean).map(decodeURIComponent)
  if (parts[0] === 'workbench' && parts[1]) return parts[1]
  if (parts[0] === 'designers' && parts[2]) return parts[2]
  return ''
}

export default function SqlBuilderProvider({ children }: { children: ReactNode }) {
  const [active, setActive] = useState<{ request: SqlBuilderRequest; project: string } | null>(null)
  const current = useProjectStore((s) => s.currentProject?.application.folder_name || '')

  const open = useCallback((request: SqlBuilderRequest) => {
    const project = request.project || current || projectFromUrl()
    if (!project) {
      toast.error('Open a project first — the query builder needs its database.')
      return
    }
    // A field that names no layout is remembered by where it is (the screen's
    // address) and what it is (its title / mode).
    const layoutKey = request.layoutKey
      || `${window.location.pathname}#${request.title || request.mode || 'select'}${request.contextTables?.length ? `@${request.contextTables.join(',')}` : ''}`
    setActive({ request: { ...request, layoutKey }, project })
  }, [current])

  const launcher = useMemo<SqlBuilderLauncher>(() => ({ open }), [open])

  return (
    <SqlBuilderLauncherContext.Provider value={launcher}>
      {children}
      {active && (
        <Suspense fallback={null}>
          <QueryBuilderModal request={active.request} project={active.project} onClose={() => setActive(null)} />
        </Suspense>
      )}
    </SqlBuilderLauncherContext.Provider>
  )
}
